/**
 * RAMS wizard config — data-driven step definitions.
 *
 * Phase 20 (Refactor 3). Mirrors the step ordering in
 * `RAMS_WIZARD_STEPS` (../wizard-schemas/rams.ts) and the existing
 * mobile screen `apps/mobile/screens/wizards/WizardRamsScreen.tsx`.
 *
 * Step renderer assignment:
 *   - `context`   → data-driven (4 text/date fields)
 *   - `hazards`   → custom (`RamsHazardBuilder` — multi-select with
 *                   severity chips + add/remove sheet picker)
 *   - `controls`  → custom (`RamsControlsBuilder` — one control per
 *                   hazard, with severity badge)
 *   - `ppe`       → data-driven (checklist of 8 PPE items)
 *   - `method`    → data-driven (textarea + AI suggest button)
 *   - `emergency` → data-driven (textarea + text + AI suggest)
 *   - `signoff`   → data-driven (textarea with helper)
 *   - `preview`   → custom (`RamsPreview` — wraps `WizardPreview`)
 *
 * AI section-fill is declared as `disabled` because the mobile section-
 * suggest dispatcher returns 405 for RAMS sections (Phase 09b deferral).
 * The renderer wires `AISectionSuggestionButton` with `disabled={true}`
 * + the existing `aiUnavailableReason` copy so the affordance stays
 * visible without pretending to work.
 */
import type { WizardConfig } from "./types";

const PPE_OPTIONS = [
  { value: "hardHat", labelKey: "wizards.rams.ppeLabels.hardHat" },
  { value: "hiVis", labelKey: "wizards.rams.ppeLabels.hiVis" },
  { value: "safetyBoots", labelKey: "wizards.rams.ppeLabels.safetyBoots" },
  { value: "gloves", labelKey: "wizards.rams.ppeLabels.gloves" },
  { value: "eyeProtection", labelKey: "wizards.rams.ppeLabels.eyeProtection" },
  { value: "earProtection", labelKey: "wizards.rams.ppeLabels.earProtection" },
  { value: "respirator", labelKey: "wizards.rams.ppeLabels.respirator" },
  { value: "harness", labelKey: "wizards.rams.ppeLabels.harness" },
] as const;

export const RAMS_WIZARD_CONFIG: WizardConfig = {
  documentType: "RAMS",
  steps: [
    {
      id: "context",
      titleKey: "wizards.rams.stepTitles.context",
      descriptionKey: "wizards.rams.stepDescriptions.context",
      schemaRef: "RAMS.steps.context",
      fields: [
        {
          id: "jobDescription",
          type: "text",
          labelKey: "wizards.rams.jobDescriptionLabel",
          required: true,
          multiline: true,
        },
        {
          id: "siteAddress",
          type: "text",
          labelKey: "wizards.rams.siteAddressLabel",
          required: true,
        },
        {
          id: "startDate",
          type: "date",
          labelKey: "wizards.rams.startDateLabel",
          helperKey: "wizards.rams.datePickerHelper",
        },
        {
          id: "endDate",
          type: "date",
          labelKey: "wizards.rams.endDateLabel",
          helperKey: "wizards.rams.dateFormatHelper",
        },
      ],
    },
    {
      id: "hazards",
      titleKey: "wizards.rams.stepTitles.hazards",
      descriptionKey: "wizards.rams.stepDescriptions.hazards",
      schemaRef: "RAMS.steps.hazards",
      customRenderer: "RamsHazardBuilder",
    },
    {
      id: "controls",
      titleKey: "wizards.rams.stepTitles.controls",
      descriptionKey: "wizards.rams.stepDescriptions.controls",
      schemaRef: "RAMS.steps.controls",
      customRenderer: "RamsControlsBuilder",
    },
    {
      id: "ppe",
      titleKey: "wizards.rams.stepTitles.ppe",
      descriptionKey: "wizards.rams.stepDescriptions.ppe",
      schemaRef: "RAMS.steps.ppe",
      fields: [
        {
          id: "ppe",
          type: "checklist",
          labelKey: "wizards.rams.stepTitles.ppe",
          required: true,
          options: PPE_OPTIONS,
        },
      ],
    },
    {
      id: "method",
      titleKey: "wizards.rams.stepTitles.method",
      descriptionKey: "wizards.rams.stepDescriptions.method",
      schemaRef: "RAMS.steps.method",
      fields: [
        {
          id: "methodStatement",
          type: "text",
          labelKey: "wizards.rams.methodLabel",
          placeholderKey: "wizards.rams.methodPlaceholder",
          required: true,
          multiline: true,
          aiRewritePlain: true,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "RAMS",
        sectionKey: "methodStatement",
        targetFieldId: "methodStatement",
        disabled: true,
        disabledReasonKey: "wizards.rams.aiUnavailableReason",
      },
    },
    {
      id: "emergency",
      titleKey: "wizards.rams.stepTitles.emergency",
      descriptionKey: "wizards.rams.stepDescriptions.emergency",
      schemaRef: "RAMS.steps.emergency",
      fields: [
        {
          id: "emergencyProcedures",
          type: "text",
          labelKey: "wizards.rams.emergencyLabel",
          placeholderKey: "wizards.rams.emergencyPlaceholder",
          required: true,
          multiline: true,
        },
        {
          id: "assemblyPoint",
          type: "text",
          labelKey: "wizards.rams.assemblyPointLabel",
          required: true,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "RAMS",
        sectionKey: "emergencyProcedures",
        targetFieldId: "emergencyProcedures",
        disabled: true,
        disabledReasonKey: "wizards.rams.aiUnavailableReason",
      },
    },
    {
      id: "signoff",
      titleKey: "wizards.rams.stepTitles.signoff",
      descriptionKey: "wizards.rams.stepDescriptions.signoff",
      schemaRef: "RAMS.steps.signoff",
      fields: [
        {
          id: "signOffList",
          type: "text",
          labelKey: "wizards.rams.signOffLabel",
          helperKey: "wizards.rams.signOffHelper",
          required: true,
          multiline: true,
        },
      ],
    },
    {
      id: "preview",
      titleKey: "wizards.rams.stepTitles.preview",
      descriptionKey: "wizards.rams.stepDescriptions.preview",
      schemaRef: "RAMS.steps.preview",
      customRenderer: "RamsPreview",
    },
  ],
};
